import { useState } from "react";
import Visibility from "@mui/icons-material/Visibility";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { dayjs } from "@thalamos/common";
import type { Section12DoctorAdminSummary } from "@thalamos/aspire-common";

import { apiHooks } from "../../api.js";
import { routeFns } from "../../routePaths.js";
import {
  Banner,
  BannerList,
  LoadingSpinner,
} from "~/components/design-system/index.js";

// ---------------------------------------------------------------------------
// Presentational component — fully testable via Storybook args
// ---------------------------------------------------------------------------

export type Section12DoctorsManagementPageInnerProps = {
  doctors: Section12DoctorAdminSummary[];
  totalCount: number;
  loading: boolean;
  error: boolean;
  filterQuery: string;
  onFilterQueryChange: (query: string) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onViewProfile: (doctorId: string) => void;
};

export function Section12DoctorsManagementPageInner({
  doctors,
  totalCount,
  loading,
  error,
  filterQuery,
  onFilterQueryChange,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onViewProfile,
}: Section12DoctorsManagementPageInnerProps) {
  if (error) {
    return (
      <Banner
        title="Failed to load S12 doctors."
        bannerType={BannerList.ERROR}
      />
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        S12 Doctors
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        All doctors with a Section 12 accreditation record. Click the view
        button to open their profile.
      </Typography>

      <TextField
        placeholder="Filter by name, email or GMC number..."
        sx={{ width: "100%", mb: 2 }}
        size="small"
        value={filterQuery}
        onChange={(e) => onFilterQueryChange(e.target.value)}
      />

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>GMC Number</TableCell>
                  <TableCell>S12 Expiry</TableCell>
                  <TableCell>Specialisms</TableCell>
                  <TableCell>Local Authorities</TableCell>
                  <TableCell>Phone Verified</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {doctors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {filterQuery
                        ? "No doctors match your search."
                        : "No S12 doctors found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  doctors.map((doctor) => (
                    <TableRow key={doctor.id}>
                      <TableCell>{doctor.name}</TableCell>
                      <TableCell>{doctor.email}</TableCell>
                      <TableCell>{doctor.gmcNumber ?? "—"}</TableCell>
                      <TableCell>
                        {doctor.section12ExpiryDate
                          ? dayjs(doctor.section12ExpiryDate).format(
                              "DD/MM/YYYY",
                            )
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                        >
                          {doctor.specialisms.map((s) => (
                            <Chip
                              key={s.id}
                              label={s.specialismName}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                          {doctor.specialisms.length === 0 && "—"}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                        >
                          {doctor.localAuthorities.map((la) => (
                            <Chip
                              key={la.locationId}
                              label={la.locationName}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                          {doctor.localAuthorities.length === 0 && "—"}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={doctor.phoneVerified ? "Yes" : "No"}
                          size="small"
                          color={doctor.phoneVerified ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Profile">
                          <IconButton
                            size="small"
                            onClick={() => onViewProfile(doctor.id)}
                            aria-label={`view profile for ${doctor.name}`}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_event, newPage) => onPageChange(newPage)}
            onRowsPerPageChange={(event) =>
              onRowsPerPageChange(parseInt(event.target.value, 10))
            }
          />
        </>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Container — wires the API hook to the presentational component
// ---------------------------------------------------------------------------

export function Section12DoctorsManagementPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filterQuery, setFilterQuery] = useState("");

  const [{ data, loading }] = apiHooks.section12.searchDoctorsAdmin(
    filterQuery,
    rowsPerPage,
    page * rowsPerPage,
  );

  return (
    <Section12DoctorsManagementPageInner
      doctors={data?.results ?? []}
      totalCount={data?.count ?? 0}
      loading={loading}
      error={!loading && !data}
      filterQuery={filterQuery}
      onFilterQueryChange={(query) => {
        setFilterQuery(query);
        setPage(0);
      }}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={setPage}
      onRowsPerPageChange={(newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0);
      }}
      onViewProfile={(doctorId) => {
        navigate(routeFns.userProfilePage(doctorId));
      }}
    />
  );
}
